/**
 * P2.B Aspekt-System — Build-defining identity layer above Augment-Tree.
 *
 * The player picks 1 Primary Aspect (always) + 1 Secondary Aspect (unlocked
 * at Tau-Vault clear). Each Aspect provides 3 passive effects + 1 unique
 * Active Skill that can be slotted into the player's active-skill pool.
 *
 * Affinity: equipment can be tagged with `aspectAffinity` (e.g. HK-Set →
 * 'bulwark'). When the player wears 3/6/8 pieces matching a slotted aspect,
 * their passives get a 1.5×/1.75×/2.0× multiplier. Affinity applies to BOTH
 * primary and secondary aspects equally.
 *
 * Aspekt-Level 1..5: each level scales the passive baseValues. Investment
 * costs scale exponentially (2.5k → 8k → 25k → 70k CD per level after L1).
 *
 * The passive logic itself is heterogeneous — see EffectKind for the routing
 * map (P2.B.2 wires each kind into the combat tick).
 */

export type AspectId = 'bulwark' | 'pyromant' | 'stormcaller' | 'vampire' | 'phantom' | 'engineer';
export type AspectSlot = 'primary' | 'secondary';

export type AspectActiveSkillId =
  | 'frost_nova' | 'fireball' | 'lightning_bolt' | 'execute'
  | 'phantom_burst' | 'engineer_overload';

export type AspectAffinityTag = AspectId;

/** Effect-kinds drive WHERE the passive is applied in the combat-tick. See
 *  P2.B.2 for the call-site map. */
export type AspectEffectKind =
  // Stat-mults (calcPlayerStats):
  | 'hp_pct' | 'def_pct' | 'atk_pct' | 'crit_chance'
  // Combat events:
  | 'lifesteal'             // +N% heal of damage dealt
  | 'dot'                   // apply damage-over-time on hit (N% ATK / sec)
  | 'aura'                  // tick AOE damage around player (N% ATK / sec)
  | 'on_kill'               // trigger on enemy death (explosion, heal, etc.)
  | 'on_hit'                // every successful hit (stacks, etc.)
  | 'on_special'            // every special-cast (heal, bonus damage)
  | 'attack_speed_stack'    // hit-counter stacks AS-mult, decays after N sec
  | 'static_charge'         // every Nth hit triggers AOE
  | 'chain_extend'          // special-cast hits +N extra targets
  // P3 additions (Phantom + Engineer):
  | 'attack_speed_pct'      // permanent flat AS-mult (vs Stormcaller's stack-based AS)
  | 'hp_regen';             // per-second HP heal in combat (% of maxHp / sec)

export interface AspectPassiveDef {
  id: string;
  nameKey: string;
  iconKey: string;        // Lucide icon-registry key
  effectKind: AspectEffectKind;
  /** Tuple of 5 values, one per aspect-level [L1, L2, L3, L4, L5]. */
  values: [number, number, number, number, number];
  /** Optional secondary value (e.g. DoT pct + duration; aura pct + radius).
   *  When present, len === 5 same as `values`. */
  values2?: [number, number, number, number, number];
  /** Affinity-bonus multiplier applied to `values` when matched (1.5× = base
   *  for 3+ pieces, 1.75× for 6+, 2.0× for 8). Default falls back to the
   *  level value × tier-multiplier in resolvePassive(). */
  affinityBonus?: number;
}

export interface AspectDef {
  id: AspectId;
  nameKey: string;
  themeColor: string;
  /** Unlock criterion — player must meet ALL of these. */
  unlock: {
    /** Minimum highestVaultTier. Bulwark = 0 (default unlock). */
    vaultTier: number;
  };
  affinityTag: AspectAffinityTag;
  passives: [AspectPassiveDef, AspectPassiveDef, AspectPassiveDef];
  /** The active skill contributed to the player's pool when this aspect has
   *  been slotted at least once. Players can swap actives independently of
   *  current slot once unlocked. */
  activeSkillId: AspectActiveSkillId;
}

// ── Aspect-Level Cost Table (cumulative CD) ──────────────────────
// L1 is free at first unlock; L2-L5 cost CD per spec.
export const ASPECT_LEVEL_COST_CD: Record<number, number> = {
  2: 2500,
  3: 8000,
  4: 25000,
  5: 70000,
};

// ── The 4 launch aspects ──────────────────────────────────────────

export const ASPECTS: Record<AspectId, AspectDef> = {
  bulwark: {
    id: 'bulwark',
    nameKey: 'aspect.bulwark',
    themeColor: '#6b7280',
    unlock: { vaultTier: 0 },
    affinityTag: 'bulwark',
    passives: [
      {
        id: 'iron_skin',
        nameKey: 'aspect.bulwark.iron_skin',
        iconKey: 'shield',
        effectKind: 'def_pct',
        values: [0.08, 0.11, 0.14, 0.17, 0.20],
      },
      {
        id: 'thorns',
        nameKey: 'aspect.bulwark.thorns',
        iconKey: 'shield_check',
        effectKind: 'on_hit',
        // Reflect-% of damage taken back at attacker (DEF-ignored when affinity).
        values: [0.05, 0.07, 0.10, 0.12, 0.15],
      },
      {
        id: 'last_stand',
        nameKey: 'aspect.bulwark.last_stand',
        iconKey: 'heart',
        // Below 30% HP (50% with affinity): +N% DEF + lifesteal.
        effectKind: 'def_pct',
        values: [0.25, 0.30, 0.38, 0.44, 0.50],
        values2: [0.10, 0.12, 0.15, 0.18, 0.20], // lifesteal-side
      },
    ],
    activeSkillId: 'frost_nova',
  },

  pyromant: {
    id: 'pyromant',
    nameKey: 'aspect.pyromant',
    themeColor: '#f97316',
    unlock: { vaultTier: 2 },
    affinityTag: 'pyromant',
    passives: [
      {
        id: 'burning_hits',
        nameKey: 'aspect.pyromant.burning_hits',
        iconKey: 'sparkles',
        effectKind: 'dot',
        // Damage-per-second + duration (sec).
        values: [0.05, 0.08, 0.11, 0.13, 0.15],
        values2: [3.0, 3.25, 3.5, 3.75, 4.0],
      },
      {
        id: 'fire_aura',
        nameKey: 'aspect.pyromant.fire_aura',
        iconKey: 'sparkles',
        effectKind: 'aura',
        // ATK% / sec  +  radius (units).
        values: [0.02, 0.03, 0.04, 0.05, 0.06],
        values2: [2.5, 2.75, 3.0, 3.25, 3.5],
      },
      {
        id: 'combustion',
        nameKey: 'aspect.pyromant.combustion',
        iconKey: 'sparkles',
        effectKind: 'on_kill',
        // Explosion-damage %ATK + radius. Affinity grants survivors a DoT.
        values: [0.30, 0.45, 0.60, 0.70, 0.80],
        values2: [2.0, 2.25, 2.5, 2.75, 3.0],
      },
    ],
    activeSkillId: 'fireball',
  },

  stormcaller: {
    id: 'stormcaller',
    nameKey: 'aspect.stormcaller',
    themeColor: '#3b82f6',
    unlock: { vaultTier: 3 },
    affinityTag: 'stormcaller',
    passives: [
      {
        id: 'static_charge',
        nameKey: 'aspect.stormcaller.static_charge',
        iconKey: 'zap',
        effectKind: 'static_charge',
        // Every Nth hit (values2) triggers an AOE at (values * ATK).
        values: [0.80, 0.90, 1.00, 1.10, 1.20],
        values2: [4, 4, 3, 3, 3],
      },
      {
        id: 'overcharge',
        nameKey: 'aspect.stormcaller.overcharge',
        iconKey: 'zap',
        effectKind: 'attack_speed_stack',
        // % AS per stack + max-stacks.
        values: [0.03, 0.04, 0.04, 0.05, 0.06],
        values2: [5, 6, 7, 7, 8],
      },
      {
        id: 'lightning_rod',
        nameKey: 'aspect.stormcaller.lightning_rod',
        iconKey: 'zap',
        effectKind: 'chain_extend',
        // Extra chain-targets on special + damage% per chain.
        values: [1, 1, 2, 2, 3],
        values2: [0.60, 0.70, 0.70, 0.80, 0.80],
      },
    ],
    activeSkillId: 'lightning_bolt',
  },

  vampire: {
    id: 'vampire',
    nameKey: 'aspect.vampire',
    themeColor: '#dc2626',
    unlock: { vaultTier: 4 },
    affinityTag: 'vampire',
    passives: [
      {
        id: 'blood_tap',
        nameKey: 'aspect.vampire.blood_tap',
        iconKey: 'heart',
        effectKind: 'lifesteal',
        values: [0.03, 0.04, 0.05, 0.065, 0.08],
      },
      {
        id: 'feast',
        nameKey: 'aspect.vampire.feast',
        iconKey: 'heart',
        effectKind: 'on_kill',
        // Heal %MaxHP on kill. With affinity: +1% MaxHP permanent/run, cap 20%.
        values: [0.02, 0.03, 0.035, 0.045, 0.05],
      },
      {
        id: 'soul_drain',
        nameKey: 'aspect.vampire.soul_drain',
        iconKey: 'sparkles',
        effectKind: 'on_special',
        // Heal %MaxHP on special-cast.
        values: [0.08, 0.10, 0.12, 0.15, 0.18],
      },
    ],
    activeSkillId: 'execute',
  },

  // ── P3 additions ──────────────────────────────────────────────────
  phantom: {
    id: 'phantom',
    nameKey: 'aspect.phantom',
    themeColor: '#a855f7',
    unlock: { vaultTier: 4 },
    affinityTag: 'phantom',
    passives: [
      {
        // Crit-focused, complements the augment-driven crit stat. Phantom
        // builds rely on crit-mult swings to break through high-defense
        // late-game bosses where flat ATK% hits diminishing returns.
        id: 'critical_edge',
        nameKey: 'aspect.phantom.critical_edge',
        iconKey: 'sparkles',
        effectKind: 'crit_chance',
        values: [0.06, 0.09, 0.12, 0.15, 0.18],
      },
      {
        // Speed = more crit-rolls per second = more lucky burst.
        id: 'spectral_speed',
        nameKey: 'aspect.phantom.spectral_speed',
        iconKey: 'zap',
        effectKind: 'attack_speed_pct',
        values: [0.08, 0.12, 0.16, 0.20, 0.25],
      },
      {
        // Pure damage multiplier as the third leg. Reusing atk_pct keeps
        // it stackable with augments + items without new combat plumbing.
        id: 'killing_blow',
        nameKey: 'aspect.phantom.killing_blow',
        iconKey: 'crosshair',
        effectKind: 'atk_pct',
        values: [0.10, 0.15, 0.20, 0.25, 0.30],
      },
    ],
    activeSkillId: 'phantom_burst',
  },

  engineer: {
    id: 'engineer',
    nameKey: 'aspect.engineer',
    themeColor: '#f59e0b',
    unlock: { vaultTier: 5 },
    affinityTag: 'engineer',
    passives: [
      {
        // Tank passive — stacks with Bulwark's iron_skin for a true
        // defensive primary+secondary combo.
        id: 'reinforced_plating',
        nameKey: 'aspect.engineer.reinforced_plating',
        iconKey: 'shield_plus',
        effectKind: 'def_pct',
        values: [0.07, 0.10, 0.13, 0.17, 0.22],
      },
      {
        // The Engineer's signature sustain — passive in-combat regen
        // independent of damage dealt (vs Vampire's lifesteal which
        // needs hits to fire). Reads as % of maxHp per second.
        id: 'repair_protocol',
        nameKey: 'aspect.engineer.repair_protocol',
        iconKey: 'heart_pulse',
        effectKind: 'hp_regen',
        values: [0.01, 0.015, 0.02, 0.027, 0.035],
      },
      {
        // Counter-Strike: % chance for the next hit AFTER being hit to
        // deal doubled damage. Implemented as an on_hit trigger that
        // sets a "counter-charged" flag consumed on the next swing.
        // For MVP we route through atk_pct as a flat damage multiplier
        // — future polish can convert to the proper proc-on-hurt path.
        id: 'counter_strike',
        nameKey: 'aspect.engineer.counter_strike',
        iconKey: 'swords',
        effectKind: 'atk_pct',
        values: [0.05, 0.07, 0.10, 0.13, 0.16],
      },
    ],
    activeSkillId: 'engineer_overload',
  },
};

// ── Affinity-tier multipliers ────────────────────────────────────
// 0 matching pieces → 1.0× (baseline), 3-5 → 1.5×, 6-7 → 1.75×, 8+ → 2.0×.
// Both primary and secondary aspect share the same affinity-table; an item
// can only carry ONE aspectAffinity so a fully-built loadout will at most
// max out one of the two slots.
export function affinityMultiplier(matchingPieces: number): number {
  if (matchingPieces >= 8) return 2.0;
  if (matchingPieces >= 6) return 1.75;
  if (matchingPieces >= 3) return 1.5;
  return 1.0;
}

/** Resolve a passive's effective value at a given aspect-level with current
 *  affinity tier applied. `level` is 1..5 (0 = aspect not active). */
export function resolvePassiveValue(
  passive: AspectPassiveDef,
  level: number,
  matchingPieces: number,
  useSecondaryValue: boolean = false,
): number {
  if (level <= 0) return 0;
  const idx = Math.max(0, Math.min(4, level - 1));
  const base = useSecondaryValue && passive.values2 ? passive.values2[idx] : passive.values[idx];
  return base * affinityMultiplier(matchingPieces);
}

/** Cost in CD to advance from `currentLevel` to `currentLevel + 1`. L1 is
 *  free (granted on first slot of an aspect); L2-L5 use ASPECT_LEVEL_COST_CD. */
export function aspectLevelUpCost(currentLevel: number): number {
  return ASPECT_LEVEL_COST_CD[currentLevel + 1] ?? 0;
}

/** True if the player's current vault-tier meets the aspect's unlock gate. */
export function isAspectUnlocked(aspectId: AspectId, highestVaultTier: number): boolean {
  return highestVaultTier >= ASPECTS[aspectId].unlock.vaultTier;
}

/** Sum all passive-values of a given effect-kind across BOTH currently-
 *  slotted aspects, with affinity-tier multiplier applied per aspect.
 *
 *  Both primary and secondary contribute equally — each gets multiplied by
 *  its own affinity tier (so a player wearing 8 HK pieces + Bulwark primary
 *  + Pyromant secondary maxes out Bulwark's bonus but not Pyromant's, since
 *  affinity tags are unique-per-item).
 *
 *  Returns 0 if no aspects active or no matching effect. Used by combat
 *  call sites (calcPlayerStats, autoTick) to apply build-defining bonuses.
 */
export function totalAspectValue(
  player: import('./types').Player,
  effect: AspectEffectKind,
  useSecondaryValue: boolean = false,
  getItem?: (id: string) => import('./types').ItemDef | undefined,
): number {
  const aspects = player.aspects;
  if (!aspects) return 0;
  let total = 0;
  // Affinity counts shared across both slots.
  const counts = getItem ? countAffinityPieces(player.equipment, getItem) : {} as Partial<Record<AspectId, number>>;
  const slotted: Array<AspectId | null> = [aspects.primary, aspects.secondary];
  for (const aspectId of slotted) {
    if (!aspectId) continue;
    const def = ASPECTS[aspectId];
    const level = aspects.levels[aspectId] ?? 0;
    if (level <= 0) continue;
    const matching = counts[aspectId] ?? 0;
    for (const passive of def.passives) {
      if (passive.effectKind !== effect) continue;
      total += resolvePassiveValue(passive, level, matching, useSecondaryValue);
    }
  }
  return total;
}

/** Aspect-specific resolver: returns the value of ONE named passive on ONE
 *  named aspect, with current level + affinity tier applied. Returns 0 if
 *  the aspect is not slotted or is at L0.
 *
 *  This exists because multiple aspects share an `effectKind` (e.g. Vampire
 *  Feast and Pyromant Combustion both use `on_kill`) but mean different things
 *  semantically. Combat-tick code that needs to apply one specific passive's
 *  effect must look it up by id, not by effect-kind. */
export function resolvePassiveById(
  player: import('./types').Player,
  aspectId: AspectId,
  passiveId: string,
  getItem: (id: string) => import('./types').ItemDef | undefined,
  useSecondaryValue: boolean = false,
): number {
  const aspects = player.aspects;
  if (!aspects) return 0;
  // Must be slotted (primary OR secondary) — non-slotted aspects don't apply.
  if (aspects.primary !== aspectId && aspects.secondary !== aspectId) return 0;
  const level = aspects.levels[aspectId] ?? 0;
  if (level <= 0) return 0;
  const passive = ASPECTS[aspectId].passives.find((p) => p.id === passiveId);
  if (!passive) return 0;
  const counts = countAffinityPieces(player.equipment, getItem);
  const matching = counts[aspectId] ?? 0;
  return resolvePassiveValue(passive, level, matching, useSecondaryValue);
}

/** Count equipped pieces matching each aspect-affinity tag. Mirrors the
 *  `countSetPieces` pattern in itemSets.ts. Used by AspectPanel + combat-tick
 *  to derive the active affinity multiplier. */
export function countAffinityPieces(
  equipment: import('./types').EquippedMap,
  getItem: (id: string) => import('./types').ItemDef | undefined,
): Partial<Record<AspectId, number>> {
  const counts: Partial<Record<AspectId, number>> = {};
  for (const inst of Object.values(equipment)) {
    if (!inst) continue;
    const def = getItem(inst.itemId);
    if (def?.aspectAffinity) {
      counts[def.aspectAffinity] = (counts[def.aspectAffinity] ?? 0) + 1;
    }
  }
  return counts;
}

/** Default empty aspects state for fresh players. Bulwark auto-unlocks at L1
 *  so the player always has at least one valid primary pick. */
export function defaultAspectsState() {
  return {
    primary: 'bulwark' as AspectId,
    secondary: null as AspectId | null,
    levels: { bulwark: 1, pyromant: 0, stormcaller: 0, vampire: 0, phantom: 0, engineer: 0 } as Record<AspectId, number>,
    unlocked: ['bulwark'] as AspectId[],
  };
}
